import { IntlShape } from 'react-intl';
import { CatalogItem } from '../../../../../../redux/api/fymAPI.types';
import {
  filterPickerFiles,
  PickerFile,
  PickerFilter,
} from '../../../../../music24/promote/composer/picker/pickerFiles';
import { MediaBlockPick, pickOfCover, pickOfPickerFile } from './mediaBlockPick';

/** The files of one song that the rail can list. `item` is undefined while the catalog loads. */
export interface MediaBlockSongFiles {
  catalogItemUuid: string;
  item: CatalogItem | undefined;
  files: PickerFile[];
}

/** One tile of the rail: a Studio file or the cover of a song. */
export interface MediaBlockTile {
  /** Unique over every song: `<catalogItemUuid>/cover` or `<catalogItemUuid>/<picker file key>`. */
  key: string;
  pick: MediaBlockPick;
  /** "r2" for a Studio file; undefined for a cover. */
  revision: number | undefined;
}

/** The tiles of one song, with the title for the song heading of "All songs". */
export interface MediaBlockSongTiles {
  catalogItemUuid: string;
  title: string;
  tiles: MediaBlockTile[];
}

function coverTile(intl: IntlShape, song: MediaBlockSongFiles): MediaBlockTile | null {
  if (song.item === undefined) {
    return null;
  }
  const pick = pickOfCover(intl, song.item);
  return pick === null ? null : { key: `${song.catalogItemUuid}/cover`, pick, revision: undefined };
}

function fileTile(intl: IntlShape, song: MediaBlockSongFiles, file: PickerFile): MediaBlockTile | null {
  const pick = pickOfPickerFile(intl, file, song.catalogItemUuid);
  return pick === null ? null : { key: `${song.catalogItemUuid}/${file.key}`, pick, revision: file.revision };
}

/**
 * The tiles of each song for the Images / Videos filter: the cover first (an image), then the
 * newest ready Studio files, newest first. A song with no tile is left out. Pure data, so it is
 * tested without React.
 */
export function mediaBlockSongTiles(
  intl: IntlShape,
  songs: MediaBlockSongFiles[],
  filter: PickerFilter
): MediaBlockSongTiles[] {
  return songs
    .map((song) => {
      const cover = filter === 'video' ? null : coverTile(intl, song);
      const files = filterPickerFiles(song.files, filter)
        .map((file) => fileTile(intl, song, file))
        .filter((tile): tile is MediaBlockTile => tile !== null);
      return {
        catalogItemUuid: song.catalogItemUuid,
        title: song.item?.title ?? '',
        tiles: cover === null ? files : [cover, ...files],
      };
    })
    .filter((song) => song.tiles.length > 0);
}

/** The number of tiles of every song with no filter: zero means the empty state, not the "no images" line. */
export function mediaBlockTileCount(intl: IntlShape, songs: MediaBlockSongFiles[]): number {
  return mediaBlockSongTiles(intl, songs, 'all').reduce((count, song) => count + song.tiles.length, 0);
}
